import config from "Configurations";
import { apiRequest } from "util/services";

// Thin wrappers around the Sonic backend blueprint (api/sonic.py).
// All calls use getFullResult=true so callers can inspect { error } payloads.

export const fetchRules = () => apiRequest("sonic/rules", "GET", null, true);

export const createRule = (payload) =>
  apiRequest("sonic/rules", "POST", payload, true);

export const updateRule = (id, payload) =>
  apiRequest(`sonic/rules/${id}`, "PUT", payload, true);

export const setRuleStatus = (id, status) =>
  apiRequest(`sonic/rules/${id}/status`, "POST", { status }, true);

export const deleteRule = (id) =>
  apiRequest(`sonic/rules/${id}`, "DELETE", null, true);

export const fetchTemplates = () =>
  apiRequest("sonic/templates", "GET", null, true);

export const createTemplate = (payload) =>
  apiRequest("sonic/templates", "POST", payload, true);

export const updateTemplate = (id, payload) =>
  apiRequest(`sonic/templates/${id}`, "PUT", payload, true);

export const fetchActivities = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", params.limit);
  if (params.status) q.set("status", params.status);
  if (params.channel) q.set("channel", params.channel);
  if (params.segment) q.set("segment", params.segment);
  if (params.since) q.set("since", params.since);
  if (params.until) q.set("until", params.until);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return apiRequest(`sonic/activities${suffix}`, "GET", null, true);
};

export const fetchStats = (params = {}) => {
  const q = new URLSearchParams();
  if (params.days) q.set("days", params.days);
  if (params.channel) q.set("channel", params.channel);
  if (params.segment) q.set("segment", params.segment);
  if (params.since) q.set("since", params.since);
  if (params.until) q.set("until", params.until);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return apiRequest(`sonic/stats${suffix}`, "GET", null, true);
};

export const approveActivity = (id) =>
  apiRequest(`sonic/activities/${id}/approve`, "POST", {}, true);

export const rejectActivity = (id, reason) =>
  apiRequest(`sonic/activities/${id}/reject`, "POST", { reason }, true);

export const fetchSettings = () =>
  apiRequest("sonic/settings", "GET", null, true);

export const updateSettings = (payload) =>
  apiRequest("sonic/settings", "PUT", payload, true);

// Voice Agent sandbox — "Test your Voice Agent".
export const fetchVerifiedNumbers = () =>
  apiRequest("sonic/voice/verified-numbers", "GET", null, true);

export const confirmNumberVerification = (phone, label, firebaseIdToken) =>
  apiRequest(
    "sonic/voice/verify/confirm",
    "POST",
    { phone, label, firebase_id_token: firebaseIdToken },
    true
  );

export const startTestCall = (payload) =>
  apiRequest("sonic/voice/test-call", "POST", payload, true);

export const sendTestTemplateMessage = (payload) =>
  apiRequest("sonic/templates/test-message", "POST", payload, true);

export const fetchTestCall = (id) =>
  apiRequest(`sonic/voice/test-call/${id}`, "GET", null, true);

// Browser test: talk to the same agent through the microphone, no phone line.
export const startBrowserTest = (payload) =>
  apiRequest("sonic/voice/browser-test", "POST", payload, true);

export const attachBrowserConversation = (id, conversationId) =>
  apiRequest(
    `sonic/voice/browser-test/${id}/conversation`,
    "POST",
    { conversation_id: conversationId },
    true
  );

// ElevenLabs may need a moment before the new conversation can be looked up.
export const attachBrowserConversationWhenReady = async (id, conversationId) => {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const res = await attachBrowserConversation(id, conversationId);
    if (res && res.attached) return;
    if (!res || res.reason !== "conversation_not_ready") return;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
};

// A finished test call's audio (MP3) as an ArrayBuffer, or null. The recording is
// private to the org, so it is fetched with the session token rather than linked.
export const fetchTestCallRecording = async (id) => {
  let token = null;
  try {
    token = JSON.parse(window.localStorage.getItem("jwt_token") || "{}").value || null;
  } catch (e) {
    token = null;
  }
  const res = await fetch(`${config.BASE_URL}sonic/voice/test-call/${id}/recording`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  return res.arrayBuffer();
};

// Feedback Capture instruments; a test call can ask a survey's questions.
export const fetchCallSurveys = () =>
  apiRequest("feedback/instruments", "GET", null, true);

export const fetchElevenLabsVoices = (language) => {
  const q = language ? `?language=${encodeURIComponent(language)}` : "";
  return apiRequest(`sonic/voice/elevenlabs/voices${q}`, "GET", null, true);
};

// WhatsApp Business connection (Meta Embedded Signup).
export const fetchWhatsappConnectConfig = () =>
  apiRequest("sonic/whatsapp/connect-config", "GET", null, true);

export const connectWhatsapp = (payload) =>
  apiRequest("sonic/whatsapp/connect", "POST", payload, true);

export const disconnectWhatsapp = () =>
  apiRequest("sonic/whatsapp/disconnect", "POST", {}, true);

export const connectSms = () => apiRequest("sonic/sms/connect", "POST", {}, true);

export const disconnectSms = () => apiRequest("sonic/sms/disconnect", "POST", {}, true);

export const fetchIntegrations = () =>
  apiRequest("sonic/integrations", "GET", null, true);

export const connectIntegration = (provider) =>
  apiRequest(`sonic/integrations/${provider}/connect`, "POST", {}, true);

export const disconnectIntegration = (provider) =>
  apiRequest(`sonic/integrations/${provider}/disconnect`, "POST", {}, true);

export const updateIntegration = (provider, payload) =>
  apiRequest(`sonic/integrations/${provider}`, "PUT", payload, true);

export const fetchTeam = () => apiRequest("sonic/team", "GET", null, true);

export const upsertTeamMember = (payload) =>
  apiRequest("sonic/team", "POST", payload, true);

export const deleteTeamMember = (id) =>
  apiRequest(`sonic/team/${id}`, "DELETE", null, true);
